import api from './api';
import userApi from '../users/api';
import { DateTime } from 'luxon';
import i18n from '@/plugins/i18n';
import router from '../../router';
import { nextTick } from 'vue'; // Imported for Vue 3 compatibility
import { cloneDeep, isEqual } from 'lodash';

//Path Regex to check if socket listener should be used
const pathRegex = /^\/planning/;
const solutionPathRegex = /^\/solution/;

let updatedDutiesBuffer = [];
let updateDutyTimer = null;
let deleteDutyTimer = null;
let queuedDutiesToDelete = [];
let createDutyTimer = null;
let queuedDutiesToCreate = [];

const state = {
	initLoading: {
		tableRendered: false,
		dataReceived: false,
	},
	isValidForm: { dutyPlanningForm: true },
	loading: false,
	asking: false,
	planningError: {
		error: false,
		message: '',
	},
	days: {},
	daysRef: {},
	daysIdRef: {},
	drivers: [],
	driversRef: {},
	forceUpdate: 0,
	duties: {},
	changedDuty: {},
	changedDutyProperties: [],
	absences: [],
	updatedDuties: {},
	dutyList: {},
	dayStatusUpdated: 0,
	statistics: {},
	unallocatedDuties: {},
	unallocatedDutiesVisible: false,
	unallocatedUsers: {},
	absenceStatus: {},
	firstLoad: true,
	dutyType: {},
	recentDuties: [],
	singleStatistics: {},
	extraPayCurrency: 'CHF',
	statisticsByStatus: {},
	socketQueue: [],
	isSolutionState: false,
	deletedDuties: [],
	allDeletedDutiesLoaded: false,
	currentChunk: 0,
	agreements: [],
	agreementsByDuty: {},
	update: 0,
	manualWorktimeBalances: [],
	histories: [],
	selectedDutyDetails: {},
};

const getters = {
	isDriverActive: (state) => (driver, date) => {
		const dutyDate = new Date(date);
		const minimumTimestamp = 0;
		const maximumTimestamp = '9999-12-31';
		const joiningDate = new Date(driver.dateOfEntry ?? minimumTimestamp);
		const leavingDate = new Date(driver.dateOfLeaving ?? maximumTimestamp);
		return dutyDate >= joiningDate && dutyDate <= leavingDate;
	},
	getDayStatus: (state) => (day) => {
		return state.days[day]?.data?.status ?? '';
	},
	isDayStatusPastOrPublished: (state, getters) => (day) => {
		const status = getters.getDayStatus(day);
		return ['past', 'published'].includes(status);
	},
	canReadDutyForDate: (state, getters, rootState, rootGetters) => (isoDate) => {
		const dayStatus = state.days[isoDate]?.data?.status;

		const canReadDuties = rootGetters['auth/checkPermission']('duties.read');
		const canReadInactive = rootGetters['auth/checkPermission']('roster.read.inactive');
		const canReadActive = rootGetters['auth/checkPermission']('roster.read.active');
		if (!canReadDuties) {
			return false;
		} else if (canReadActive && canReadInactive) {
			return true;
		} else if (!dayStatus) {
			console.warn('Could not find any day status while checking read permissions.');
			return false;
		}
		const belongsToActiveRoster = ['past', 'published'].includes(dayStatus);

		if (belongsToActiveRoster) {
			return canReadActive;
		}

		return canReadInactive;
	},
	canEditDutyForDate: (state, getters, rootState, rootGetters) => (isoDate) => {
		const dayStatus = state.days[isoDate]?.data?.status;

		if (dayStatus === 'locked') {
			return false;
		}

		const canEditDuties = rootGetters['auth/checkPermission']('duties.edit');
		const canEditActive = rootGetters['auth/checkPermission']('roster.edit.active');
		const canEditInactive = rootGetters['auth/checkPermission']('roster.edit.inactive');

		if (!canEditDuties) {
			return false;
		} else if (canEditDuties && canEditActive && canEditInactive) {
			return true;
		} else if (!dayStatus) {
			console.warn('Could not find any day status.');
			return false;
		}
		const belongsToActiveRoster = ['past', 'published'].includes(dayStatus);

		if (belongsToActiveRoster) {
			return canEditActive;
		}

		return canEditInactive;
	},
};

const mutations = {
	setIsValidDutyForm(state, value) {
		state.isValidForm.dutyPlanningForm = value;
	},
	setInitLoading(state, { tableRendered, dataReceived }) {
		if (tableRendered === true) {
			state.firstLoad = false;
		}
		if (typeof tableRendered === 'boolean') {
			state.initLoading.tableRendered = tableRendered;
		}

		if (typeof dataReceived === 'boolean') {
			state.initLoading.dataReceived = dataReceived;
		}
	},

	setLoading(state, status) {
		state.loading = status;
	},

	setPlanningError(state, { error, message }) {
		state.planningError.error = error;
		state.planningError.message = message;
	},

	setAsking(state, status) {
		state.asking = status;
	},

	setDays(state, days) {
		if (days) {
			const newDays = { ...state.days };
			const newDaysRef = { ...state.daysRef };
			const newDaysIdRef = { ...state.daysIdRef };

			for (const key in days) {
				newDays[days[key].isoDate] = days[key];
				if (days[key].data) {
					newDaysRef[days[key].data.id] = days[key].data.dateString;
					newDaysIdRef[days[key].data.dateString] = days[key].data.id;
				}
			}
			state.days = newDays;
			state.daysRef = newDaysRef;
			state.daysIdRef = newDaysIdRef;
		}
	},

	clearDays(state) {
		state.days = {};
		state.daysRef = {};
		state.daysIdRef = {};
	},

	setDrivers(state, drivers) {
		if (drivers) {
			const newDriversRef = { ...state.driversRef };
			for (const driver of drivers) {
				newDriversRef[driver.id] = driver;
			}
			state.driversRef = newDriversRef;
			state.drivers = drivers;
		}
	},

	setDuties(state, { duties, skipUpdate }) {
		if (duties) {
			const newDuties = { ...state.duties };
			const newDutyList = { ...state.dutyList };
			const newUpdatedDuties = { ...state.updatedDuties };

			duties.forEach((d) => {
				if (newDuties[d.user]) {
					newDuties[d.user] = { ...newDuties[d.user], [state.daysRef[d.day]]: d };
				} else {
					newDuties[d.user] = { [state.daysRef[d.day]]: d };
				}
				newDutyList[d.id] = d;

				if (!skipUpdate) {
					let id = d.user + '_' + d.day;
					newUpdatedDuties[id] = (newUpdatedDuties[id] ?? -1) + 1;
				}
			});

			state.duties = newDuties;
			state.dutyList = newDutyList;
			if (!skipUpdate) {
				state.updatedDuties = newUpdatedDuties;
			}
		}
	},

	addDutyToList(state, duty) {
		state.dutyList[duty.id] = duty;
	},

	setAbsenceColorForDuty(state, { user, day, absenceStatus, skipUpdate }) {
		if (!state.absenceStatus[user]) {
			state.absenceStatus[user] = {};
		}
		state.absenceStatus[user][day] = absenceStatus;
		if (!skipUpdate) {
			const id = user + '_' + state.daysIdRef[day];
			// Replaced Vue.set with logic below
			state.updatedDuties[id] = state.updatedDuties[id] === undefined ? 0 : state.updatedDuties[id] + 1;
		}
	},

	setAbsenceColors(state, absenceColors) {
		if (absenceColors?.length) {
			const newAbsenceStatus = { ...state.absenceStatus };
			for (const absenceColor of absenceColors) {
				if (!newAbsenceStatus[absenceColor.user]) {
					newAbsenceStatus[absenceColor.user] = {};
				} else {
					newAbsenceStatus[absenceColor.user] = { ...newAbsenceStatus[absenceColor.user] };
				}
				newAbsenceStatus[absenceColor.user][absenceColor.day] = absenceColor.status;
			}
			state.absenceStatus = newAbsenceStatus;
		}
	},

	setEmptyDuties(state, emptyDuties) {
		const newDuties = { ...state.duties };
		for (const duty of emptyDuties) {
			if (newDuties[duty.driver]) {
				newDuties[duty.driver] = { ...newDuties[duty.driver], [duty.day]: { empty: true } };
			} else {
				newDuties[duty.driver] = { [duty.day]: { empty: true } };
			}
		}
		state.duties = newDuties;
	},

	setSelectedDutyDetails(state, duty) {
		state.selectedDutyDetails = duty;
	},

	updateDuty(state, { duty, skipUpdate = false }) {
		if (state.duties[duty.user]) {
			state.duties[duty.user][state.daysRef[duty.day]] = JSON.parse(JSON.stringify(duty));
			state.dutyList[duty.id] = JSON.parse(JSON.stringify(duty));
		} else {
			let newDuty = {};
			newDuty[state.daysRef[duty.day]] = JSON.parse(JSON.stringify(duty));
			state.duties[duty.user] = newDuty;
			state.dutyList[duty.id] = JSON.parse(JSON.stringify(duty));
		}

		if (!skipUpdate) {
			const id = duty.user + '_' + duty.day;
			// Replaced Vue.set
			state.updatedDuties[id] = state.updatedDuties[id] === undefined ? 0 : state.updatedDuties[id] + 1;
		}
	},

	clearDuty(state, { duty, removeFromList = true, skipUpdate = false }) {
		const userDuties = state.duties[duty.user];
		if (userDuties) {
			const dutyDayRef = state.daysRef?.[duty.day];
			if (dutyDayRef && userDuties[dutyDayRef]?.id && userDuties[dutyDayRef].id === duty.id) {
				userDuties[dutyDayRef] = {};
			}
			if (removeFromList) {
				delete state.dutyList[duty.id];
			}
			if (!skipUpdate) {
				const id = duty.user + '_' + duty.day;
				// Replaced Vue.set
				state.updatedDuties[id] = state.updatedDuties[id] === undefined ? 0 : state.updatedDuties[id] + 1;
			}
		}
	},

	unallocateDuty(state, { duty }) {
		if (state.dutyList[duty.id]) {
			state.dutyList[duty.id].user = null;
			state.dutyList[duty.id].fixed = false;
			state.dutyList[duty.id].fixedBy = null;
		}
	},

	setDutyActivities(state, { dutyActivities }) {
		state.dutyActivities = dutyActivities;
	},

	testChangeDuties(state, dutyObject) {
		for (const key of Object.keys(dutyObject)) {
			// Replaced Vue.set
			state.changedDuty[key] = dutyObject[key];
		}
	},

	setChangedDutyProperties(state, prop) {
		Object.entries(prop).forEach(([key, value]) => {
			const index = state.changedDutyProperties.findIndex((item) => key in item);

			if (index !== -1) {
				state.changedDutyProperties[index] = { [key]: value };
			} else {
				state.changedDutyProperties.push({ [key]: value });
			}
		});
	},
	resetChangedDutyProperties(state) {
		state.changedDutyProperties = [];
	},
	clearMoveDutyState(state) {
		state.changedDuty = {};
	},

	setAbsences(state, { absences, skipUpdate = false }) {
		if (absences) {
			const newAbsences = { ...state.absences };
			absences.forEach((absence) => {
				let driverId = absence.user;
				let absenceObj = newAbsences[driverId] ? { ...newAbsences[driverId] } : {};

				if (absenceObj[absence.id]) {
					absenceObj[absence.id] = { ...absenceObj[absence.id], ...absence };
				} else {
					absenceObj[absence.id] = absence;
				}
				newAbsences[driverId] = absenceObj;
			});
			state.absences = newAbsences;
		}
	},

	deleteAbsence(state, { absenceId }) {
		for (const key in state.absences) {
			let userAbsences = state.absences[key];

			if (userAbsences[absenceId]) {
				delete userAbsences[absenceId];
			}
		}
	},

	setUpdateDuties(state, { id, duty, skipUpdate }) {
		// Update duty object
		if (state.duties[duty.user] && state.daysRef[duty.day] && state.duties[duty.user][state.daysRef[duty.day]]) {
			state.duties[duty.user][state.daysRef[duty.day]] = duty;
		}
		state.dutyList[duty.id] = duty;
		if (!skipUpdate) {
			if (!state.updatedDuties[id] && state.updatedDuties[id] != 0) {
				// Replaced Vue.set
				state.updatedDuties[id] = 0;
			} else {
				let newVal = state.updatedDuties[id];
				newVal += 1;
				// Replaced Vue.set
				state.updatedDuties[id] = newVal;
			}
		}
	},

	clearUpdatedDuties(state) {
		state.updatedDuties = {};
	},

	setDayStatus(state, day) {
		if (day.status) {
			const date = Object.keys(state.days).find((date) => state.days[date].data?.id == day.id);
			if (date) {
				state.days[date].data.status = day.status;
			}
			state.dayStatusUpdated++;
		}
	},

	setStatistics(state, { statistics }) {
		if (!state.statistics) {
			state.statistics = {};
		}

		const batchUpdates = {};

		statistics.forEach((s) => {
			const cached = state.statistics[s.user];

			if (cached && isEqual(chached, s)) {
				return;
			}

			batchUpdates[s.user] = s;
		});

		Object.assign(state.statistics, batchUpdates);
	},

	clearStatistics(state) {
		state.statistics = {};
	},

	setUnallocatedDuties(state, { duties }) {
		const newUnallocatedDuties = { ...state.unallocatedDuties };
		const newDutyList = { ...state.dutyList };

		duties.forEach((d) => {
			let dayDuties = newUnallocatedDuties[d.day] ? { ...newUnallocatedDuties[d.day] } : {};
			dayDuties[d.id] = d;

			newDutyList[d.id] = d;
			newUnallocatedDuties[d.day] = dayDuties;
		});

		state.unallocatedDuties = newUnallocatedDuties;
		state.dutyList = newDutyList;
		state.unallocatedDutiesVisible = true;
	},

	setUnallocatedUsers(state, { users }) {
		if (users) {
			state.unallocatedUsers = {};
			for (const user of users) {
				state.unallocatedUsers[user] = true;
			}
		}
	},

	updateUnallocatedDuties(state, { duty }) {
		const dutyISODate = state.daysRef[duty.day];
		const dayStatus = state.days[dutyISODate]?.data?.status || '';
		const skipUpdate = !(duty.type == 'duty' || (duty.type == 'reserve' && (dayStatus == 'preview' || dayStatus == 'planning')));
		if (skipUpdate) {
			return;
		}
		let days = state.unallocatedDuties[duty.day];
		if (!days) {
			days = {};
		}
		if (Object.hasOwn(duty, 'exchange') && state.dutyList[duty.id]) {
			// Replaced Vue.set
			state.dutyList[duty.id].exchange = duty.exchange;
		}
		if (Object.hasOwn(duty, 'deleted') && state.dutyList[duty.id]) {
			// Replaced Vue.set
			state.dutyList[duty.id].deleted = duty.deleted;
		}
		if (Object.hasOwn(duty, 'name') && state.dutyList[duty.id]) {
			// Replaced Vue.set
			state.dutyList[duty.id].name = duty.name;
		}
		if (days[duty.id]) {
			// Replaced Vue.set
			state.unallocatedDuties[duty.day][duty.id].style = duty.style;
			state.unallocatedDuties[duty.day][duty.id].exchange = duty.exchange;
		} else {
			// Corrected logic here
			days[duty.id] = duty;
			let id = duty.user + '_' + duty.day;
			if (state.updatedDuties[id] === undefined) {
				state.updatedDuties = { ...state.updatedDuties, [id]: 0 };
			} else {
				state.updatedDuties = {
					...state.updatedDuties,
					[id]: state.updatedDuties[id] + 1,
				};
			}
			if (!state.dutyList[duty.id]) {
				// Replaced Vue.set
				state.dutyList[duty.id] = duty;
			}
		}
		const updateDutiesForDay = {
			...(state.unallocatedDuties[duty.day] ?? {}),
			[duty.id]: duty,
		};
		state.unallocatedDuties = {
			...state.unallocatedDuties,
			[duty.day]: updateDutiesForDay,
		};
	},

	deleteUnallocatedDuty(state, { duty }) {
		const newDutiesForDay = state.unallocatedDuties[duty.day];
		if (newDutiesForDay) {
			delete newDutiesForDay[duty.id];
		}

		state.unallocatedDuties = {
			...state.unallocatedDuties,
			[duty.day]: newDutiesForDay,
		};
	},

	deleteUnallocatedDuties(state, duties) {
		const newUnallocated = { ...state.unallocatedDuties };
		for (const duty of duties) {
			if (!newUnallocated[duty.day]) continue;

			// shallow clone day bucket only when first touched
			if (newUnallocated[duty.day] === state.unallocatedDuties[duty.day]) {
				newUnallocated[duty.day] = { ...state.unallocatedDuties[duty.day] };
			}

			delete newUnallocated[duty.day][duty.id];
		}
		state.unallocatedDuties = newUnallocated;
	},

	setDaysHidden(state, days) {
		for (const day of days) {
			if (state.days[day]) {
				state.days[day].isHidden = true;
			}
		}
	},

	hideSpecificDays(state, dayIds) {
		const days = [];
		for (const dayId of dayIds) {
			if (state.daysRef[dayId]) {
				days.push(state.daysRef[dayId]);
			}
		}

		for (const day of days) {
			state.days[day].isHidden = true;
		}
	},

	setDaysVisible(state, daysToUpdate) {
		for (const date of Object.keys(state.days)) {
			if ((!daysToUpdate || daysToUpdate.includes(date)) && state.days[date].isHidden) {
				state.days[date].isHidden = false;
			}
		}
	},

	forceUpdate(state) {
		state.forceUpdate++;
	},

	setDutyType(state, type) {
		state.dutyType = type;
	},

	setRecentDuties(state, recentDuties) {
		state.recentDuties = recentDuties.map((duty) => {
			return { ...duty._id, date: duty.dayObject ? duty?.dayObject?.date : duty.day };
		});
	},

	setSingleStatistics(state, statistics) {
		if (statistics.length) {
			state.singleStatistics = statistics[0];
		}
	},

	setExtraPayCurrency(state, currency) {
		state.extraPayCurrency = currency;
	},

	setStatisticsByStatus(state, { type, statistics }) {
		state.statisticsByStatus[type] = {};
		for (const statistic of statistics) {
			if (state.statisticsByStatus[type][statistic.day] === undefined) {
				state.statisticsByStatus[type][statistic.day] = {};
			}
			state.statisticsByStatus[type][statistic.day][statistic.user] = statistic;
		}
	},

	addItemToSocketQueue(state, socketQueueItem) {
		state.socketQueue.push(socketQueueItem);
	},
	setDeletedDuty(state, duty) {
		const dutyISODate = state.daysRef[duty.day];
		const day = state.days[dutyISODate]?.data;
		state.deletedDuties.unshift({ ...duty, day });
	},
	setDeletedDuties(state, duties) {
		const deletedDutiesBeginning = [];
		const deletedDutiesEnd = [];
		for (const duty of duties) {
			if (duty.day === null) {
				continue;
			}
			if (!state.dutyList[duty.id]) {
				let insertToBeginning = false;
				if (state.deletedDuties.length) {
					const newDeletedAt = new Date(duty.deletedAt);
					const deletedAt = new Date(state.deletedDuties[0].deletedAt);
					if (newDeletedAt > deletedAt) {
						insertToBeginning = true;
					}
				}
				if (insertToBeginning) {
					deletedDutiesBeginning.push(duty);
				} else {
					deletedDutiesEnd.push(duty);
				}
			}

			state.dutyList[duty.id] = { ...duty, day: duty.day?.id ? duty.day?.id : duty.day?._id };
		}
		state.deletedDuties = deletedDutiesBeginning.concat(state.deletedDuties).concat(deletedDutiesEnd);
	},

	removeDeletedDuty(state, index) {
		state.deletedDuties.splice(index, 1);
	},

	setAllDeletedDutiesLoaded(state) {
		state.allDeletedDutiesLoaded = true;
	},

	nextChunk(state) {
		state.currentChunk++;
	},

	clearSocketQueue(state) {
		state.socketQueue = [];
	},

	setSolutionState(state, isSolutionState) {
		state.isSolutionState = isSolutionState;
	},

	setAgreements(state, agreements) {
		const filteredAgreements = [];
		const agreementsByDuty = { ...state.agreementsByDuty };
		agreements.forEach((agreement) => {
			if (agreement.key === 'solution.violation.acknowledge') {
				filteredAgreements.push(agreement);
				if (!agreementsByDuty[agreement.duty]) {
					agreementsByDuty[agreement.duty] = [];
				} else {
					agreementsByDuty[agreement.duty] = [...agreementsByDuty[agreement.duty]];
				}
				agreementsByDuty[agreement.duty].push(agreement);
			}
		});

		state.agreementsByDuty = agreementsByDuty;
		state.agreements = state.agreements.concat(filteredAgreements);
	},

	setHistories(state, histories) {
		state.histories = histories;
	},

	addAgreement(state, agreement) {
		if (agreement.key === 'solution.violation.acknowledge') {
			state.agreements.push(agreement);
			const agreementsByDuty = state.agreementsByDuty[agreement.duty] ? [...state.agreementsByDuty[agreement.duty]] : [];
			agreementsByDuty.push(agreement);
			// Replaced Vue.set
			state.agreementsByDuty[agreement.duty] = agreementsByDuty;
			state.update++;
		}
	},

	clearState(state) {
		if (state.isSolutionState && solutionPathRegex.test(router.currentRoute._value.path)) {
			return;
		}
		state.duties = {};
		state.dutyList = {};
		state.updatedDuties = {};
		state.unallocatedDuties = {};
		state.deletedDuties = [];
		state.currentChunk = 0;
		state.allDeletedDutiesLoaded = false;
		state.unallocatedUsers = {};
		state.days = {};
		state.daysRef = {};
		state.daysIdRef = {};
		state.recentDuties = [];
		state.drivers = [];
		state.driversRef = {};
		state.statistics = {};
		state.singleStatistics = {};
		state.statisticsByStatus = {};
		state.absences = [];
		state.absenceStatus = {};
		state.socketQueue = [];
		state.firstLoad = true;
	},
	setManualWorktimeBalances(state, manualWorktimeBalances) {
		state.manualWorktimeBalances = manualWorktimeBalances;
	},
	setUserToNullOnDelete(state, duty) {
		state.dutyList[duty.id].user = null;
	},
	setDeletedToTrueOnDelete(state, duty) {
		state.dutyList[duty.id].deleted = true;
	},
};

const actions = {
	async load({ state, commit, dispatch }, { firstDate, cellHeight, preRender, yIndex, showInactiveDrivers, loadForSolutions, driver, numberOfDays }) {
		commit('setInitLoading', { dataReceived: false });
		return await dispatch('init', {
			firstDate,
			cellHeight,
			preRender,
			yIndex,
			showInactiveDrivers,
			loadForSolutions,
			solutionDriver: driver,
			numberOfDays,
		});
	},

	async loadManualWorktimeBalances({ state, commit, dispatch }, user) {
		try {
			let result = await userApi.getManualWorktimeBalances(user);
			if (result) {
				result = result.data.map(({ type, deleted, user, day, amount, id, reason }) => ({
					type,
					deleted,
					user,
					day: day,
					amount: amount,
					id,
					reason,
				}));

				commit('setManualWorktimeBalances', result);
				return state.manualWorktimeBalances;
			}
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not load manual work time balances.' });
			dispatch('setError', { error: true, message });
		}
	},

	async loadFullDuty({ commit }, { id, setSelectedDutyDetails = true }) {
		const result = await api.findDuty(id);
		if (result.data?.internalNotes === undefined) {
			result.data.internalNotes = null;
		}

		if (setSelectedDutyDetails) {
			await commit('setSelectedDutyDetails', result.data);
		}
		return result.data;
	},

	async loadDutiesWithRootID({ commit }, rootID) {
		const result = await api.findDutiesWithRootID(rootID);
		return result.data;
	},

	async loadFullDutWithExchangeInfo({ commit }, id) {
		const result = await api.findDutyWithExchanges(id);
		return result.data;
	},

	async loadSingleStatistics({ commit, dispatch }, { date, user }) {
		try {
			const result = await api.loadStatistics(date, [user]);
			commit('setSingleStatistics', result.data);
			return result.data;
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not load single statistics.' });
			dispatch('setError', { error: true, message });
		}
	},

	async loadExtraPayCurrency({ commit }) {
		try {
			const result = await api.loadExtraPayCurrency();
			commit('setExtraPayCurrency', result.data);
		} catch (err) {
			commit('setError', { error: true, message: err.response.data.message });
		}
	},

	async loadDutyActivities({ state, commit }, { dutyId }) {
		return api
			.findDutyActivities(dutyId)
			.then((response) => {
				const data = response.data;

				commit('setDutyActivities', { dutyActivities: data });
			})
			.catch((err) => {
				const message = err.response.data.message;
				console.warn(message);
			});
	},

	async loadDutiesByDay({ state, dispatch, commit }, { day }) {
		try {
			const isDay = DateTime.fromISO(day).isValid;

			if (day && isDay) {
				let duties = await dispatch('loadDuties', { from: day, to: day, reduced: true });

				if (duties?.data.length > 0) {
					//Add styles to duties
					for (let duty of duties.data) {
						const style = await dispatch('createDutyStyle', duty);
						duty.style = style;
					}
				}

				await commit('setDuties', { duties: duties?.data, skipUpdate: true });
				await dispatch('fillDuties', { from: day, to: day });
			}
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not load the duties by day.' });
			dispatch('setError', { error: true, message: i18n.t('error_generic', { message }) });
		}
	},

	async loadRecentDuties({ commit, dispatch }) {
		try {
			const result = await api.findRecentDuties();

			commit('setRecentDuties', result.data);
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not load the recent duties.' });
			dispatch('setError', { error: true, message: i18n.t('error_generic', { message }) });
		}
	},

	clearMoveDuty({ state, commit }) {
		commit('clearMoveDutyState');
	},

	applyDefaultLabels({ commit }, { labels }) {
		api.applyDefaultLabels(labels);
	},

	clearDutyActivities({ state, commit }) {
		commit('setDutyActivities', { dutyActivities: [] });
	},

	changeDuty({ state, commit, dispatch }, dutyObj) {
		commit('testChangeDuties', dutyObj);
	},

	setChangedDutyProperties({ state, commit }, prop) {
		commit('setChangedDutyProperties', prop);
	},

	async updateDuty({ state, commit }, { dutyId, changedDuty, changeComment = undefined }) {
		changedDuty.changeComment = changeComment || undefined;
		delete changedDuty.modified;
		let result = await api.updateDuty(dutyId, changedDuty);
		commit('resetChangedDutyProperties');
		return result;
	},

	async updateRootDuties({ commit }, duties) {
		let result = await api.updateRootDuties(duties);
		commit('resetChangedDutyProperties');
		return result;
	},

	async deleteDuty({ state, commit }, { dutyId, changeComment }) {
		let result = await api.deleteDuty({ dutyId, changeComment });
		commit('resetChangedDutyProperties');
		return result;
	},

	async createNewDuty({ commit }, { duty }) {
		if (!duty.name) {
			switch (duty.type) {
				case 'compensation':
					duty.name = 'A';
					break;
				case 'rest':
					duty.name = 'R';
					break;
				case 'vacation':
					duty.name = 'F';
					break;
				case 'reserve':
					duty.name = 'SD';
					break;
				default:
					duty.name = 'D';
					break;
			}
		}

		if (!duty.user) {
			duty.user = null;
		}

		return api
			.createDuty(duty)
			.then((response) => {
				commit('resetChangedDutyProperties');
				return response;
			})
			.catch((err) => {
				const message = err.response.data.message;
				console.warn(message);
			});
	},

	resetChangedDutyProperties({ commit }) {
		commit('resetChangedDutyProperties');
	},

	async init(
		{ state, rootState, commit, dispatch },
		{ firstDate, cellHeight, preRender, yIndex, showInactiveDrivers, loadForSolutions, solutionDriver, numberOfDays = 80 }
	) {
		try {
			if (loadForSolutions) {
				commit('clearDays');
			}

			const specialFromCases = ['past', 'published', 'planning'];
			let from, to;
			if (!specialFromCases.includes(firstDate)) {
				// FirstDate is a date -> Create 'from' date object
				from = DateTime.fromISO(firstDate);
				if (numberOfDays !== 'published' && numberOfDays !== 'planning') {
					// NumberOfDays is a date -> Create 'to' date object
					to = DateTime.fromISO(firstDate).plus({ days: numberOfDays });
				}
			} else if (numberOfDays !== 'published' && numberOfDays !== 'planning') {
				// NumberOfDays is a date -> Create 'from' and 'to' date objects
				from = DateTime.now();
				to = DateTime.now().plus({ days: numberOfDays });
			}

			commit('setLoading', true);

			if (loadForSolutions) {
				commit('setSolutionState', true);
			}
			let days = await dispatch('loadDays', {
				from: specialFromCases.includes(firstDate) ? firstDate : from.toISODate(),
				to: ['published', 'planning'].includes(numberOfDays) ? numberOfDays : to.toISODate(),
				loadForSolutions,
			}).catch((err) => {
				const message = dispatch('extractErrorMessage', { error: err, fallback: i18n.t('action_ok') });
				dispatch('setError', { error: true, message });
			});
			let dayIds = [];
			if (days?.data) {
				dayIds = days.data?.map((d) => {
					return d._id;
				});
			}

			if (!loadForSolutions) {
				await dispatch('getUnallocatedDuties', { days: dayIds });
			}

			if (to === undefined && days?.data && days.data.length !== 0) {
				to = DateTime.fromISO(days.data.at(-1).date);
			}

			if (from === undefined && days?.data && days.data.length !== 0) {
				from = DateTime.fromISO(days.data.at(0).date);
			}

			if (from === undefined && to === undefined) {
				const message = i18n.t('error_no_day_right');
				dispatch('setError', { error: true, message });
			}

			dispatch('buildDays', { days, from, to, isInit: true });

			let drivers = [];
			if (loadForSolutions && state.drivers.length) {
				drivers = state.drivers;
			} else {
				drivers = await dispatch('loadDrivers');
				commit('setDrivers', drivers);
			}

			let count = preRender * 2;
			count = Math.min(drivers.length, count);

			let listDriverIds = drivers.slice(Math.max(yIndex, 0), count + yIndex).map(({ id }) => {
				return id;
			});

			let filteredDrivers = [];
			if (loadForSolutions) {
				filteredDrivers = [...drivers];
			} else {
				let planningDataRaw = localStorage.getItem('planningData');
				let planningData = JSON.parse(planningDataRaw);
				const searchFilter = planningData?.searchFilter ? planningData.searchFilter : '';

				const pattern = /\s*([^,]+)\s*/g;
				const matches = searchFilter.match(pattern);

				if (matches) {
					for (const match of matches) {
						const filterString = match.trim();
						if (filterString || !showInactiveDrivers) {
							const driversFound = drivers.filter(
								(driver) =>
									((!showInactiveDrivers && !filterString) ||
										driver.firstName.toLowerCase().indexOf(filterString.toLowerCase()) !== -1 ||
										driver.lastName.toLowerCase().indexOf(filterString.toLowerCase()) !== -1 ||
										driver.displayName.toLowerCase().indexOf(filterString.toLowerCase()) !== -1) &&
									(showInactiveDrivers || driver.isActive)
							);

							filteredDrivers = filteredDrivers.concat(driversFound);
						}
					}
				} else if (searchFilter.trim() === '' && !showInactiveDrivers) {
					filteredDrivers = drivers.filter((driver) => driver.isActive);
				} else if (searchFilter.trim() === '') {
					filteredDrivers = drivers;
				}

				filteredDrivers = filteredDrivers.filter((obj, index) => {
					return index === filteredDrivers.findIndex((o) => obj.id === o.id);
				});
			}

			if (filteredDrivers.length > 0) {
				filteredDrivers.forEach((filteredDriver) => {
					if (listDriverIds.indexOf(filteredDriver.id) < 0) {
						listDriverIds.push(filteredDriver.id);
					}
				});
			}

			let absences = [];
			const absenceColors = [];
			const absenceColorMap = {};
			let loadAbsences = !loadForSolutions || !Object.keys(state.absences).length;
			if (loadAbsences) {
				absences = (await api.findAllAbsences()).data;

				for (const absence of absences) {
					dispatch('getAbsenceStatusPerDay', { absence, skipUpdate: true, absenceColors, absenceColorMap });
				}
			}
			commit('setAbsences', { absences, skipUpdate: true });
			commit('setAbsenceColors', absenceColors);
			let duties = await dispatch(solutionDriver ? 'loadDutiesForDrivers' : 'loadDuties', {
				from: from?.toISODate() ?? undefined,
				to: to?.toISODate() ?? undefined,
				drivers: solutionDriver ? [solutionDriver] : null,
				reduced: true,
			});

			if (duties?.data.length > 0) {
				for (let duty of duties.data) {
					const style = await dispatch('createDutyStyle', duty);
					duty.style = style;
				}
			}

			await commit('setDuties', { duties: duties?.data, skipUpdate: true });
			await dispatch('fillDuties', { from: from?.toISODate(), to: to?.toISODate() });

			commit('setLoading', false);
			commit('setInitLoading', { dataReceived: true });

			return filteredDrivers;
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not initialize the planning view.' });
			dispatch('setError', { error: true, message });
		}
	},

	splitDuty(_, payload) {
		const { id, splitBreak, changeComment } = payload;
		return api.splitDuty(id, splitBreak, changeComment);
	},
	mergeDuty(_, { dutyToMerge, targetDuty, changeComment }) {
		return api.mergeDuty(dutyToMerge, targetDuty, changeComment);
	},

	async getAbsenceStatusPerDay({ state, commit, dispatch }, { absence, skipUpdate, absenceColors, absenceColorMap }) {
		if (absence.type === 'workPreference' || absence.type === 'workAgreement') {
			return;
		}

		const currentDay = new Date(absence.startDate);
		const end = new Date(absence.endDate);

		const isApproved = absence.status === 'approved';
		const isPending = absence.status === 'pending';
		const isRejected = absence.status === 'rejected';

		while (currentDay.getTime() <= end.getTime()) {
			const day = currentDay.toISOString().split('T')[0];
			if (!skipUpdate) {
				commit('setAbsenceColorForDuty', { user: absence.user, day, absenceStatus: absence.status, skipUpdate });

				if (state.duties[absence.user]) {
					if (state.duties[absence.user][day]) {
						let duty = JSON.parse(JSON.stringify(state.duties[absence.user][day]));
						duty.style = await dispatch('createDutyStyle', duty);

						commit('updateDuty', { duty });
					}
				}
			} else if (absenceColors?.length !== undefined && absenceColorMap) {
				let existingAbsenceColors;
				if (absenceColorMap[absence.user]) {
					existingAbsenceColors = absenceColorMap[absence.user][day];
				}
				if (existingAbsenceColors) {
					if (existingAbsenceColors.isPending && isApproved) {
						existingAbsenceColors.status = 'approved';
					} else if (existingAbsenceColors.isRejected && !isRejected) {
						existingAbsenceColors.isRejected = isRejected;
						existingAbsenceColors.isPending = isPending;
						existingAbsenceColors.status = absence.status;
					}
				} else {
					const absenceColor = { user: absence.user, day, status: absence.status, isRejected, isPending };
					absenceColors.push(absenceColor);
					if (absenceColorMap[absence.user] === undefined) {
						absenceColorMap[absence.user] = {};
					}
					absenceColorMap[absence.user][day] = absenceColor;
				}
			}

			currentDay.setUTCDate(currentDay.getUTCDate() + 1);
		}
	},

	async loadDays({ commit, dispatch }, { from, to, loadForSolutions }) {
		try {
			if (!loadForSolutions) {
				await dispatch('loadAgreements', { from, to });
			}
			return await api.findDays(from, to);
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not load days.' });
			dispatch('setError', { error: true, message });
		}
	},

	async loadDuties({ commit, dispatch }, { from, to }) {
		try {
			return await api.findDuties(from, to);
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not load the duties.' });
			dispatch('setError', { error: true, message });
		}
	},

	async loadDutiesForDrivers({ commit, dispatch }, { from, to, drivers, reduced, skipVirtuals }) {
		try {
			return await api.findDutiesForDrivers(from, to, drivers, reduced, skipVirtuals);
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not load duties for drivers.' });
			dispatch('setError', { error: true, message });
		}
	},

	async loadDeletedDutiesChunk({ commit }, chunk) {
		const response = await api.loadDeletedDutiesChunk(chunk);
		commit('setDeletedDuties', response.data);
		if (chunk > 0 && !response.data.length) {
			commit('setAllDeletedDutiesLoaded');
		}
	},

	async restoreDuty({ commit }, { duty, index }) {
		await api.restoreDuty(duty);
		commit('removeDeletedDuty', index);
	},

	nextChunk({ commit }) {
		commit('nextChunk');
	},

	buildDays({ commit }, { days, from, to, isInit }) {
		let formattedDays = {};
		const formatedDays = to?.diff(from, 'days').values.days;
		for (var i = 0; i <= formatedDays; i++) {
			let d = from.plus({ days: i });

			let data = {};
			if (days?.data) {
				data = days.data.find((dd) => dd.dateString === d.toISODate());
			}

			formattedDays[d.toISODate()] = {
				data: data,
				isoDate: d.toISODate(),
				isHidden: !isInit,
			};
		}

		commit('setDays', formattedDays);
	},

	async getJumpData({ state, commit, dispatch, rootState }, { dateRange }) {
		const from = DateTime.fromISO(dateRange.start);
		const to = DateTime.fromISO(dateRange.end);

		const days = await dispatch('loadDays', { from: dateRange.start, to: dateRange.end }).catch((err) => {
			const message = dispatch('extractErrorMessage', { error: err, fallback: i18n.t('action_ok') });
			dispatch('setError', { error: true, message });
		});
		let dayIds = [];
		if (days?.data) {
			dayIds = days.data.map((d) => {
				return d._id;
			});
		}

		await dispatch('getUnallocatedDuties', { days: dayIds });

		dispatch('buildDays', { days: days, from: from, to: to, isInit: true });

		const duties = await dispatch('loadDuties', { from: dateRange.start, to: dateRange.end, reduced: true });

		if (duties?.data.length > 0) {
			for (let duty of duties.data) {
				const style = await dispatch('createDutyStyle', duty);
				duty.style = style;
			}
		}

		await commit('setDuties', { duties: duties?.data, skipUpdate: true });
		await dispatch('fillDuties', { from: dateRange.start, to: dateRange.end });
		commit('setLoading', false);
	},

	fillDuties({ state, commit }, { from, to }) {
		let fromDate = new Date(from);
		const toDate = new Date(to);
		const emptyDuties = [];
		state.drivers.forEach((driver) => {
			let driverDuties = state.duties[driver.id];
			if (!driverDuties) {
				state.duties[driver.id] = {};
				driverDuties = {};
			}

			while (fromDate <= toDate) {
				const d = fromDate.toISOString().split('T')[0];
				if (!driverDuties[d]) {
					emptyDuties.push({ driver: driver.id, day: d });
				}

				fromDate.setDate(fromDate.getDate() + 1);
			}
			fromDate = new Date(from);
		});

		commit('setEmptyDuties', emptyDuties);
	},

	setTableRendered({ commit }, value) {
		commit('setInitLoading', { tableRendered: value });
	},

	updateLoading({ commit }, status) {
		commit('setLoading', status);
	},

	async askData({ state, commit, dispatch, rootState }, { dateRange, force, loadDays = true, days }) {
		if ((state.asking || !state.initLoading.tableRendered || !state.initLoading.dataReceived) && !force) {
			return;
		}

		commit('setAsking', true);
		commit('setLoading', true);
		if (dateRange.start && dateRange.end && loadDays) {
			let days = await dispatch('loadDays', { from: dateRange.start, to: dateRange.end }).catch((err) => {
				const message = dispatch('extractErrorMessage', { error: err, fallback: i18n.t('action_ok') });
				dispatch('setError', { error: true, message });
			});
			let dayIds = [];
			if (days?.data) {
				dayIds = days.data?.map((d) => {
					return d._id;
				});
			}

			await dispatch('getUnallocatedDuties', { days: dayIds });

			dispatch('buildDays', { days: days, from: DateTime.fromISO(dateRange.start), to: DateTime.fromISO(dateRange.end) });
		}

		let duties = await dispatch('loadDuties', { from: dateRange.start, to: dateRange.end });

		if (duties?.data.length > 0) {
			for (let duty of duties.data) {
				const style = await dispatch('createDutyStyle', duty);
				duty.style = style;
			}
		}

		await commit('setDuties', { duties: duties?.data, skipUpdate: true });
		await dispatch('fillDuties', { from: dateRange.start, to: dateRange.end });

		if (loadDays) {
			commit('forceUpdate');
		}
		commit('setAsking', false);
	},

	getAbsence({ state }, { userId, absenceId }) {
		if (state.absences[userId]) {
			return state.absences[userId][absenceId];
		} else {
			return null;
		}
	},

	extractErrorMessage({ state }, { error, fallback }) {
		const message = error?.response?.data?.error?.message ?? error?.response?.data?.message ?? error?.message ?? fallback;
		return message;
	},

	setError({ commit, dispatch }, { error, message }) {
		if (solutionPathRegex.test(router.currentRoute._value.path) && error) {
			commit('solution/setError', { error: { message } }, { root: true });
		} else {
			commit('setPlanningError', { error, message });
		}
	},

	async evaluateSingleDriver({ commit }, { driverId, from, to, debug }) {
		try {
			const result = await api.evaluateSingleDriver(driverId, from, to, debug);
			return result.data;
		} catch (err) {
			if (err.response?.data?.message) {
				commit('setError', { error: true, message: err.response.data.message });
			} else {
				commit('setError', { error: true, message: err.message });
			}
			return null;
		}
	},

	async getStatistics({ commit }, { date, users }) {
		return api
			.loadStatistics(date, users)
			.then((response) => {
				const statistics = response.data;
				commit('setStatistics', { statistics });

				return state.statistics;
			})
			.catch((err) => {
				console.warn('Error getting statistics', err);
			});
	},

	async loadStatisticsByDayStatus({ commit }, dayStatus) {
		const statistics = await api.loadStatisticsByDayStatus(dayStatus);
		commit('setStatisticsByStatus', { type: dayStatus, statistics: statistics.data });
	},

	async getUnallocatedDuties({ commit, dispatch, state }, { days }) {
		try {
			const response = await api.loadUnallocatedDuties(days);
			const duties = response.data;
			for (let duty of duties) {
				const style = await dispatch('createDutyStyle', duty);
				duty.style = style;
			}

			commit('setUnallocatedDuties', { duties });

			return state.unallocatedDuties;
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not load unallocated duties' });
			dispatch('setError', { error: true, message });
		}
	},

	async getUnallocatedUsers({ commit }, { days }) {
		const result = await api.loadUnallocatedUsers(days);
		commit('setUnallocatedUsers', { users: result.data });
	},

	setDaysHidden({ commit }, days) {
		commit('setDaysHidden', days);
	},

	setDaysVisible({ commit }, daysToUpdate) {
		commit('setDaysVisible', daysToUpdate);
	},

	async createDutyStyle({ state, dispatch }, duty) {
		let style = {};
		if (duty) {
			let classes = 'planning-table-item ';
			if (duty.notes) {
				classes += 'has-note ';
			}

			if (duty.internalNotes) {
				classes += 'has-internal-note ';
			}

			if (duty.fixed) {
				classes += 'fixed ';
			}

			if (duty.exchange) {
				classes += 'exchange ';
			}

			if (duty.modified) {
				classes += 'modified ';
			}

			style.classes = classes;
			style.name = duty.nameKey ? i18n.t(duty.nameKey) : duty.name;

			const secondsToTime = (seconds) => {
				if (seconds === null || seconds === undefined) {
					return '00:00';
				}
				let hours = Math.floor(seconds / 3600);
				let minutes = Math.floor(seconds / 60) % 60;

				if (hours < 10) {
					hours = '0' + hours.toString();
				}

				if (minutes < 10) {
					minutes = '0' + minutes.toString();
				}
				return hours + ':' + minutes;
			};
			const start = secondsToTime(duty.start);
			const end = secondsToTime(duty.end);
			const standbyStart = secondsToTime(duty.standbyStart);
			const standbyEnd = secondsToTime(duty.standbyEnd);

			if (duty.start && duty.end) {
				style.time = start + ' - ' + end;
			}

			let dutyTooltip = '';

			if (style.name) {
				dutyTooltip += style.name;
			}

			if (style.time) {
				dutyTooltip += '\n' + style.time;
			}

			if (duty.shift) {
				dutyTooltip += '\n' + i18n.t('duty_shift_' + duty.shift);
			}

			if (duty.workTime) {
				const workTime = secondsToTime(duty.workTime);
				dutyTooltip += '\n' + i18n.t('duty_workTime') + ': ' + workTime;
			}

			if (duty.dutyTime) {
				const dutyTime = secondsToTime(duty.dutyTime);
				dutyTooltip += '\n' + i18n.t('duty_dutyTime') + ': ' + dutyTime;
			}

			if (duty.standbyStart && duty.standbyEnd) {
				dutyTooltip += '\n' + i18n.t('duty_standby') + ': ' + standbyStart + ' - ' + standbyEnd;
			}

			if (duty.notes || duty.internalNotes) {
				dutyTooltip += '\n';
			}

			if (duty.internalNotes) {
				dutyTooltip += '\n' + duty.internalNotes;
			}

			if (duty.notes) {
				dutyTooltip += '\n' + duty.notes;
			}

			if (duty.hasViolations || duty.hasUnmet || duty.hasMet) {
				dutyTooltip += '\n';
			}

			dutyTooltip += duty.hasViolations ? '\n' + i18n.t('duty_has_violations') : '';
			if (duty.violations && state.agreements.length && state.agreementsByDuty[duty.id]?.length) {
				let violationIsApproved = true;
				if (duty.violations) {
					for (const violation of duty.violations) {
						if (!(await dispatch('violationIsApproved', { violation, agreements: state.agreementsByDuty[duty.id] }))) {
							violationIsApproved = false;
							break;
						}
					}
					if (violationIsApproved) {
						dutyTooltip += i18n.t('duty_has_agreement');
					}
				}
			}
			dutyTooltip += duty.hasUnmet ? '\n' + i18n.t('duty_unmet') : '';
			dutyTooltip += duty.hasMet ? '\n' + i18n.t('duty_met') : '';

			if (duty.exchange) {
				dutyTooltip += '\n';
			}

			dutyTooltip += duty.exchange ? '\n' + i18n.t('duty_has_exchange') : '';

			if (duty?.labels && Array.isArray(duty.labels) && duty.labels.length) {
				const labelText = duty.labels.map((label) => `• ${label.name}`).join('\n');

				dutyTooltip += '\n' + i18n.t('duty_changed_property_labels') + ': ' + '\n' + labelText;
			}

			style.tooltip = dutyTooltip;

			let color = {
				background: 'transparent',
				font: 'var(--v-misc-black-base)',
				borderColor: 'transparent',
				borderBottomColor: 'transparent',
			};

			switch (duty.type) {
				case 'rest':
					if (duty.isRestSunday) {
						color.background = '#E1FBFB';
					} else {
						color.background = 'var(--v-misc-white-base)';
					}
					break;
				case 'compensation':
					color.background = 'var(--v-misc-white-base)';
					break;
				case 'vacation':
					color.font = '#B09269';
					break;
				case undefined:
					color.background = 'transparent';
					break;
				default:
					color.background = 'var(--v-grey-200-base)';
			}

			if (duty.hasMet && duty.hasUnmet) {
				color.borderBottomColor = '#13B4AA';
			} else if (duty.hasUnmet) {
				color.borderBottomColor = 'var(--v-info-lighten3)';
			} else if (duty.hasMet) {
				color.borderBottomColor = 'var(--v-success-lighten3)';
			}

			const dutyISODate = state.daysRef[duty.day];

			if (state.absenceStatus[duty.user] && state.absenceStatus[duty.user][dutyISODate]) {
				const status = state.absenceStatus[duty.user][dutyISODate];
				if (status === 'approved') {
					color.borderBottomColor = '#776347';
				} else if (status === 'pending') {
					color.borderBottomColor = '#BEA683';
				}
			}

			if (state.absences[duty.user]) {
				for (const [key, absence] of Object.entries(state.absences[duty.user])) {
					const { startDate, endDate, type, status, priority } = absence;
					if (dutyISODate >= startDate.substr(0, 10) && dutyISODate <= endDate.substr(0, 10)) {
						if (type === 'offtime' || type === 'reduce') {
							if (priority == 2 || priority == 3) {
								style.borderBottom = '5px solid transparent';
							}
						} else if (type === 'workPreference') {
							if (status === 'pending') {
								color.borderBottomColor = '#F4C430';
							} else if (status === 'approved') {
								color.borderBottomColor = 'orange';
							}
						}
					}
				}
			}

			if (duty.hasViolations) {
				let violationIsApproved = true;
				if (duty.violations) {
					for (const violation of duty.violations) {
						if (state.agreements.length && state.agreementsByDuty[duty.id]?.length) {
							if (!(await dispatch('violationIsApproved', { violation, agreements: state.agreementsByDuty[duty.id] }))) {
								violationIsApproved = false;
								break;
							}
						} else if (violation.weight) {
							violationIsApproved = false;
							break;
						}
					}
				}

				let isBottomColorDefaultError = duty.violations.some((violation) => !violation.category || violation.category === 'violation.category_law'); //This line of code is a priority check used to determine if a duty has a "high-level" or "critical" legal violation. It is looking for specific types of errors within the duty.violations array to decide which shade of red to paint the indicator bar.

				if (violationIsApproved) {
					color.borderBottomColor = 'var(--v-error-lighten2)';
				} else {
					if (isBottomColorDefaultError) {
						color.borderBottomColor = 'var(--v-error-base)';
					} else {
						color.borderBottomColor = 'var(--v-error-lighten3)';
					}
				}
			}

			style.colors = color;
		}

		return style;
	},
	async violationIsApproved({ dispatch }, { violation, agreements }) {
		for (const agreement of agreements) {
			if (
				agreement.params?.params?.messageKey === violation.messageKey &&
				(await dispatch('objectIsEqual', { a: agreement.params?.params?.messageParams, b: violation.messageParams }))
			) {
				return true;
			}
		}
		return false;
	},

	objectIsEqual({ state }, { a, b }) {
		if (!a || !b) {
			return a === b;
		}
		const keys = Object.keys(a);
		if (Object.keys(b).length !== keys.length) {
			return false;
		}

		for (const key of keys) {
			if (a[key] instanceof Date || b[key] instanceof Date) {
				const dateA = new Date(a[key]);
				const dateB = new Date(b[key]);
				if (dateA.getTime() !== dateB.getTime()) {
					return false;
				}
			} else if (a[key] !== b[key]) {
				return false;
			}
		}
		return true;
	},

	async createAgreement({ state, commit, dispatch }, agreement) {
		try {
			const result = await api.createAgreement(agreement);

			return result;
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not create the agreement' });
			dispatch('setError', { error: true, message });
		}
	},

	setDutyType({ commit }, type) {
		commit('setDutyType', type);
	},

	async unassignDuty({ commit, dispatch }, duty) {
		try {
			commit('clearDuty', { duty, removeFromList: false });
			const response = await api.updateDuty(duty.id, {
				user: null,
				fixed: false,
				fixedBy: null,
				changeComment: duty.changeComment,
			});
			return response;
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not unassign duty.' });
			dispatch('setError', { error: true, message });
		}
	},

	clearState({ commit }) {
		commit('clearState');
	},

	async addToExchange({ commit, dispatch }, dutyId) {
		try {
			const result = await api.addToExchange({ duty: dutyId, sender: null, preferences: ['noDuty'], type: 'exchange' });
			commit('dutyExchange/setNewDutyExchange', { dutyExchange: result.data }, { root: true });
			return result;
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: 'Could not add duty to exchange.' });
			dispatch('setError', { error: true, message });
		}
	},

	async loadAgreements({ commit }, { from, to }) {
		const response = await api.loadAgreements(from, to);
		commit('setAgreements', response.data);
	},

	async loadDutyHistories({ commit, dispatch }, dutyID) {
		try {
			const response = await api.loadDutyHistories(dutyID);
			commit('setHistories', response.data);
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: i18n.t('action_ok') });
			dispatch('setError', { error: true, message });
		}
	},

	async loadUserDutyHistories({ commit, dispatch }, payload) {
		try {
			const response = await api.loadUserDutyHistories(payload);
			commit('setHistories', response.data);
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: i18n.t('action_ok') });
			dispatch('setError', { error: true, message });
		}
	},

	async updateDayStatus({ commit, dispatch }, { status, id }) {
		try {
			const result = await api.updateDayStatus(id, { status });
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: i18n.t('action_ok') });
			dispatch('setError', { error: true, message });
		}
	},

	async exportRosterCsv({ commit, dispatch }, payload) {
		try {
			const response = await api.exportRosterCsv(payload);
			return response.data;
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: i18n.t('action_ok') });
			dispatch('setError', { error: true, message });
		}
	},

	async exportRosterXlsx({ commit, dispatch }, payload) {
		try {
			const response = await api.exportRosterXlsx(payload);
			return response.data;
		} catch (err) {
			const message = await dispatch('extractErrorMessage', { error: err, fallback: i18n.t('action_ok') });
			dispatch('setError', { error: true, message });
		}
	},

	//Socket-Events for Duties

	// async socket_updated_Duty({ state, commit, dispatch }, payload) {
	// 	const startTime = performance.now();
	// 	if (!pathRegex.test(router.currentRoute._value.path) && !solutionPathRegex.test(router.currentRoute._value.path)) {
	// 		return;
	// 	}

	// 	if (localStorage.getItem('autoUpdate') !== 'true') {
	// 		commit('addItemToSocketQueue', { event: 'updateDuty', payload: payload });
	// 		return;
	// 	}

	// 	const updatedDuty = payload[0].payload;
	// 	if (
	// 		state.dutyList[updatedDuty.id] &&
	// 		!((state.dutyList[updatedDuty.id].type === 'rest' || state.dutyList[updatedDuty.id].type === 'compensation') && updatedDuty.user === null)
	// 	) {
	// 		updatedDutiesBuffer.push(updatedDuty);
	// 	}

	// 	if (updateDutyTimer) {
	// 		clearTimeout(updateDutyTimer);
	// 	}

	// 	updateDutyTimer = setTimeout(async () => {
	// 		console.log(`Run ${updatedDutiesBuffer.length} updates`);
	// 		const start = new Date();
	// 		await dispatch('updateDuties', updatedDutiesBuffer);
	// 		updatedDutiesBuffer = [];
	// 		const duration = new Date() - start;
	// 		const updateTime = performance.now() - startTime;
	// 		const totalTime = duration + updateTime;
	// 		console.log(`Run completed (${totalTime.toFixed(2)} ms)`);
	// 	}, 100);
	// },

	async updateDuties({ state, commit, dispatch }, updatedDuties) {
		const skipUpdate = updatedDuties.length > 3;
		const affectedDays = {};
		const unallocatedDutiesToRemove = [];
		const dutiesToClear = [];
		for (const updatedDuty of updatedDuties) {
			let duty = null;
			if (state.dutyList[updatedDuty.id]) {
				duty = JSON.parse(JSON.stringify(state.dutyList[updatedDuty.id]));
			}

			if (duty?.day) {
				affectedDays[duty.day] = true;
			}

			if (updatedDuty.deleted === undefined && duty?.deleted === true) {
				for (const key in updatedDuty) {
					duty[key] = updatedDuty[key];
				}
				duty.style = await dispatch('createDutyStyle', duty);
				commit('addDutyToList', duty);
				continue;
			}

			if (Object.hasOwn(updatedDuty, 'exchange')) {
				duty.exchange = updatedDuty.exchange;
				duty.style = await dispatch('createDutyStyle', duty);
				if (duty.user) {
					commit('updateDuty', { duty: duty, skipUpdate: false });
				} else {
					commit('updateUnallocatedDuties', { duty: updatedDuty });
				}
			}
			if (updatedDuty?.deleted) {
				const style = await dispatch('createDutyStyle', duty);
				duty.style = style;
				if (skipUpdate) {
					dutiesToClear.push(duty);
				} else {
					dispatch('clearDutyOnDelete', duty);
					await Vue.nextTick();
				}
			} else if ((updatedDuty?.deleted === false && duty.user) || (!updatedDuty.user && duty && !duty.user && !updatedDuty.duty)) {
				for (const key in updatedDuty) {
					duty[key] = updatedDuty[key];
				}
				const style = await dispatch('createDutyStyle', duty);
				duty.style = style;
				commit('updateUnallocatedDuties', { duty });
			} else if (duty && updatedDuty.user !== null) {
				if (state.duties[duty.user] && !duty.deleted) {
					if (state.daysRef[duty.day]) {
						const duplicatedDuty = Object.values(state.duties).find((dutiesForUser) => dutiesForUser[state.daysRef[duty.day]]?.id == duty.id);
						if (duplicatedDuty && duplicatedDuty[state.daysRef[duty.day]]) {
							commit('clearDuty', { duty: duplicatedDuty[state.daysRef[duty.day]], skipUpdate });
						}
					}
				}

				for (const key in updatedDuty) {
					duty[key] = updatedDuty[key];
				}
				duty.style = await dispatch('createDutyStyle', duty);

				if (updatedDuty.user) {
					//Remove duty from unallocated list
					if (skipUpdate) {
						unallocatedDutiesToRemove.push(duty);
					} else {
						dispatch('removeDutyFromUnallocatedList', duty);
					}
				}

				if (state.duties[duty.user]) {
					if (!duty.deleted) {
						let id = duty.user + '_' + duty.day;
						commit('setUpdateDuties', { id: id, duty: duty, skipUpdate });
						await nextTick(); // Replaced Vue.nextTick
					}
					commit('updateDuty', { duty, skipUpdate });
				}
			} else if (updatedDuty.user === null && duty) {
				const updated = {
					...state.dutyList[updatedDuty.id],
					...updatedDuty,
				};
				if (updatedDuty.duty) {
					commit('clearDuty', { duty: state.dutyList[updatedDuty.duty], removeFromList: false, skipUpdate });
					commit('unallocateDuty', { duty: { id: updatedDuty.duty } });
					const dutyStyle = await dispatch('createDutyStyle', updated);
					commit('updateUnallocatedDuties', { duty: { ...state.dutyList[updatedDuty.duty], style: dutyStyle } });
				} else if (updatedDuty.id) {
					commit('clearDuty', { duty: state.dutyList[updatedDuty.id], removeFromList: false, skipUpdate });
					commit('unallocateDuty', { duty: { id: updatedDuty.id } });
					const dutyStyle = await dispatch('createDutyStyle', updated);
					commit('updateUnallocatedDuties', { duty: { ...state.dutyList[updatedDuty.id], style: dutyStyle } });
				} else {
					commit('unallocateDuty', { duty });
					duty.user = null;
					duty.fixed = false;
					duty.fixedBy = null;
					const dutyStyle = await dispatch('createDutyStyle', duty);
					commit('updateUnallocatedDuties', { duty: { ...duty, style: dutyStyle } });
				}
			} else if (duty && duty.user === null) {
				api.findDuty(updatedDuty.id).then((response) => {
					let loadedDuty = response.data;

					commit('updateUnallocatedDuties', { duty: loadedDuty });
				});
			} else if (!duty && updatedDuty.id && updatedDuty.user && state.duties[updatedDuty.user] !== undefined) {
				const dutyRecord = (await api.findDuty(updatedDuty.id)).data;
				affectedDays[dutyRecord.day] = true;
				if (dutyRecord.user && dutyRecord.day) {
					let id = dutyRecord.user + '_' + dutyRecord.day;
					commit('setUpdateDuties', { id: id, duty: dutyRecord, skipUpdate });
				}
			} else if (
				duty &&
				duty.id &&
				duty.day &&
				duty.user &&
				(updatedDuty.hasMet !== undefined || updatedDuty.hasUnmet !== undefined || updatedDuty.hasViolations !== undefined)
			) {
				duty.hasMet = updatedDuty.hasMet;
				duty.hasUnmet = updatedDuty.hasUnmet;
				duty.hasViolations = updatedDuty.hasViolations;
				duty.style = await dispatch('createDutyStyle', duty);
				let id = duty.user + '_' + duty.day;
				commit('setUpdateDuties', { id, duty, skipUpdate });
			} else if (duty && updatedDuty.fixed !== undefined) {
				duty.fixed = updatedDuty.fixed;
				duty.style = await dispatch('createDutyStyle', duty);
				commit('updateDuty', { duty, skipUpdate });
			}
		}

		if (skipUpdate) {
			dispatch('removeDutiesFromUnallocatedList', unallocatedDutiesToRemove);
			let affectedDaysForDeletion = {};
			if (dutiesToClear.length) {
				affectedDaysForDeletion = dispatch('clearDutiesOnDelete', { duties: dutiesToClear });
			}

			commit('hideSpecificDays', Object.keys({ ...affectedDays, ...affectedDaysForDeletion }));
			commit('forceUpdate');
		}
	},
	removeDutyFromUnallocatedList({ state, commit }, duty) {
		const dayDuties = state.unallocatedDuties?.[duty.day];
		if (!dayDuties) return;

		const oldUnallocatedDuty = dayDuties[duty.id];

		if (oldUnallocatedDuty) {
			commit('deleteUnallocatedDuty', { duty: oldUnallocatedDuty });
		}
	},
	removeDutiesFromUnallocatedList({ state, commit }, duties) {
		const relevantDuties = duties.filter((duty) => state.unallocatedDuties[duty.day]?.[duty.id]);
		if (relevantDuties.length) {
			commit('deleteUnallocatedDuties', relevantDuties);
		}
	},
	clearDutyOnDelete({ state, commit }, duty) {
		if (duty.user) {
			const existingDuty = state.duties[duty.user][state.daysRef[duty.day]];
			if (existingDuty && existingDuty.id == duty.id) {
				commit('clearDuty', { duty: duty, removeFromList: false });
			}
		} else {
			commit('deleteUnallocatedDuty', { duty: duty });
		}
		commit('setDeletedDuty', duty);
		commit('setUserToNullOnDelete', duty);
		commit('setDeletedToTrueOnDelete', duty);
	},
	clearDutiesOnDelete({ state, commit }, { duties, needsForceUpdate }) {
		const affectedDays = {};
		for (const duty of duties) {
			affectedDays[duty.day] = true;
			if (duty.user) {
				const existingDuty = state.duties[duty.user][state.daysRef[duty.day]];
				if (existingDuty && existingDuty.id == duty.id) {
					commit('clearDuty', { duty: duty, removeFromList: false, skipUpdate: true });
				}
			}
			commit('setDeletedDuty', duty);
			commit('setUserToNullOnDelete', duty);
			commit('setDeletedToTrueOnDelete', duty);
		}

		const unallocatedDuties = duties.filter((duty) => !duty.user && state.unallocatedDuties[duty.day]?.[duty.id]);
		commit('deleteUnallocatedDuties', unallocatedDuties);

		if (needsForceUpdate) {
			commit('hideSpecificDays', Object.keys(affectedDays));
			commit('forceUpdate');
		}

		return affectedDays;
	},

	async createdDuty({ state, commit, dispatch }, payload) {
		if (!pathRegex.test(router.currentRoute._value.path)) {
			return;
		}

		if (localStorage.getItem('autoUpdate') !== 'true') {
			commit('addItemToSocketQueue', { event: 'createDuty', payload: payload });
			return;
		}

		const createdDuty = payload[0].payload;

		if (createDutyTimer) {
			clearTimeout(createDutyTimer);
		}

		queuedDutiesToCreate.push(createdDuty);

		createDutyTimer = setTimeout(async () => {
			dispatch('createDuties', { duties: queuedDutiesToCreate, needsForceUpdate: queuedDutiesToCreate.length > 3 });
			queuedDutiesToCreate = [];
		}, 100);
	},

	async createDuties({ state, commit, dispatch }, { duties, needsForceUpdate }) {
		const affectedDays = {};
		const createdDuties = [];
		for (const createdDuty of duties) {
			if (createdDuty.user != null) {
				if (createdDuty.day) {
					affectedDays[createdDuty.day] = true;
				}
				//TODO: Maybe check also for days
				if (state.duties[createdDuty.user]) {
					const style = await dispatch('createDutyStyle', createdDuty);
					createdDuty.style = style;

					createdDuties.push(createdDuty);
				}
			} else if (createdDuty.type !== 'rest' && createdDuty.type !== 'compensation') {
				const style = await dispatch('createDutyStyle', createdDuty);
				createdDuty.style = style;
				commit('updateUnallocatedDuties', { duty: createdDuty });
			} else {
				const style = await dispatch('createDutyStyle', createdDuty);
				createdDuty.style = style;
				commit('addDutyToList', createdDuty);
			}
		}

		if (createdDuties.length) {
			if (needsForceUpdate) {
				commit('setDuties', { duties: createdDuties, skipUpdate: needsForceUpdate });
			} else {
				for (const createdDuty of createdDuties) {
					commit('setDuties', { duties: [createdDuty], skipUpdate: needsForceUpdate });

					//Force component update
					let id = createdDuty.user + '_' + createdDuty.day;
					commit('setUpdateDuties', { id: id, duty: createdDuty });
				}
			}
		}

		if (needsForceUpdate) {
			commit('hideSpecificDays', Object.keys(affectedDays));
			commit('forceUpdate');
		}
	},

	// async socket_created_Duty({ dispatch }, payload) {
	// 	await dispatch('createdDuty', payload);
	// },

	async socket_created_CompensationDuty({ dispatch }, payload) {
		await dispatch('createdDuty', payload);
	},

	// async socket_deleted_Duty({ state, commit, dispatch }, payload) {
	// 	if (!pathRegex.test(router.currentRoute._value.path) && !solutionPathRegex.test(router.currentRoute._value.path)) {
	// 		return;
	// 	}

	// 	if (localStorage.getItem('autoUpdate') !== 'true') {
	// 		commit('addItemToSocketQueue', { event: 'deleteDuty', payload: payload });
	// 		return;
	// 	}

	// 	const deletedDuty = payload[0].payload;

	// 	let duty = state.dutyList[deletedDuty.id];
	// 	if (duty) {
	// 		if (deleteDutyTimer) {
	// 			clearTimeout(deleteDutyTimer);
	// 		}

	// 		queuedDutiesToDelete.push(duty);

	// 		deleteDutyTimer = setTimeout(async () => {
	// 			if (queuedDutiesToDelete.length > 3) {
	// 				dispatch('clearDutiesOnDelete', { duties: queuedDutiesToDelete, needsForceUpdate: true });
	// 			} else {
	// 				for (const dutyToDelete of queuedDutiesToDelete) {
	// 					dispatch('clearDutyOnDelete', dutyToDelete);
	// 				}
	// 			}
	// 			queuedDutiesToDelete = [];
	// 		}, 100);
	// 	} else {
	// 		//console.log("Duty not displayed");
	// 	}
	// },

	// //Socket-Events for Absences

	async socket_updated_Absence({ state, commit, dispatch }, payload) {
		const absence = payload[0].payload;
		if (!pathRegex.test(router.currentRoute._value.path)) {
			return;
		}

		if (absence) {
			let previousAbsence = null;

			for (const key in state.absences) {
				if (state.absences[key][absence.id]) {
					previousAbsence = state.absences[key][absence.id];
				}
			}

			if (previousAbsence) {
				let newAbsence = { ...previousAbsence };

				for (const key in absence) {
					if (absence[key]) {
						newAbsence[key] = absence[key];
					}
				}

				delete previousAbsence.status;

				const absenceDayStart = new Date(newAbsence.startDate);
				if (absence.status !== undefined && absenceDayStart) {
					const day = absenceDayStart.toISOString().split('T')[0];
					commit('setAbsenceColorForDuty', { user: newAbsence.userId, day, absenceStatus: absence.status, skipUpdate: true });
				}

				dispatch('getAbsenceStatusPerDay', { absence: previousAbsence });
				dispatch('getAbsenceStatusPerDay', { absence: newAbsence });
				commit('setAbsences', { absences: [newAbsence], skipUpdate: true });
			}
		}
	},

	async socket_created_Absence({ state, commit, dispatch }, payload) {
		const absence = payload[0].payload;
		if (!pathRegex.test(router.currentRoute._value.path)) {
			return;
		}
		if (absence) {
			dispatch('getAbsenceStatusPerDay', { absence });
			commit('setAbsences', { absences: [absence], skipUpdate: true });
		}
	},

	async socket_deleted_Absence({ state, commit, dispatch }, payload) {
		const absence = payload[0].payload;
		if (!pathRegex.test(router.currentRoute._value.path)) {
			return;
		}

		if (absence) {
			delete absence.status;
			dispatch('getAbsenceStatusPerDay', { absence });
			commit('deleteAbsence', { absenceId: absence.id });
		}
	},

	async socket_created_Agreement({ state, commit, dispatch }, payload) {
		if (
			(!pathRegex.test(router.currentRoute._value.path) && !solutionPathRegex.test(router.currentRoute._value.path)) ||
			payload[0]?.payload?.key !== 'solution.violation.acknowledge'
		) {
			return;
		}

		commit('addAgreement', payload[0]?.payload);
		if (state.dutyList[payload[0]?.payload.duty]) {
			const duty = JSON.parse(JSON.stringify(state.dutyList[payload[0]?.payload.duty]));
			if (duty) {
				duty.style = await dispatch('createDutyStyle', duty);
				commit('updateDuty', { duty });
			}
		}
	},

	// //Socket-Events for Days

	// socket_updated_Day({ state, commit }, payload) {
	// 	const day = payload[0].payload;
	// 	if (!pathRegex.test(router.currentRoute._value.path) || !day) {
	// 		return;
	// 	}
	// 	commit('setDayStatus', day);
	// },

	socket_created_Day({ commit }, payload) {
		const day = payload[0].payload;
		if (!pathRegex.test(router.currentRoute._value.path) || !day) {
			return;
		}
		const formattedDay = {
			[day.dateString]: {
				data: day,
				isoDate: day.dateString,
				isHidden: false,
			},
		};

		commit('setDays', formattedDay);
	},

	// //Socket-Events for Duty Exchange

	async socket_created_Exchange({ state, commit, dispatch, rootState }, payload) {
		const exchange = payload[0].payload;
		if (!pathRegex.test(router.currentRoute._value.path) || !exchange) {
			return;
		}

		let rawDutyExchanges = rootState.dutyExchange.dutyExchanges;
		let dutyExchanges = Array.isArray(rawDutyExchanges) ? JSON.parse(JSON.stringify(rawDutyExchanges)) : Object.values(rawDutyExchanges);
		dutyExchanges.push(exchange);

		commit('dutyExchange/setDutyExchanges', { dutyExchanges: dutyExchanges }, { root: true });
	},

	async socket_updated_Exchange({ state, commit, dispatch, rootState }, payload) {
		const exchange = payload[0].payload;
		if (!pathRegex.test(router.currentRoute._value.path) || !exchange) {
			return;
		}
		let rawDutyExchanges = rootState.dutyExchange.dutyExchanges;
		let dutyExchanges = Array.isArray(rawDutyExchanges) ? JSON.parse(JSON.stringify(rawDutyExchanges)) : Object.values(rawDutyExchanges);

		dutyExchanges.forEach((dutyExchange) => {
			if (exchange.id === dutyExchange.id) {
				for (let key in exchange) {
					dutyExchange[key] = exchange[key];
				}
			}
		});

		commit('dutyExchange/setDutyExchanges', { dutyExchanges: dutyExchanges }, { root: true });
	},

	async socket_deleted_Exchange({ state, commit, dispatch, rootState }, payload) {
		const exchange = payload[0].payload;
		if (!pathRegex.test(router.currentRoute._value.path) || !exchange) {
			return;
		}

		let dutyExchanges = JSON.parse(JSON.stringify(rootState.dutyExchange.dutyExchanges));

		dutyExchanges = dutyExchanges.filter((de) => {
			return de.id === exchange.id;
		});

		commit('dutyExchange/setDutyExchanges', { dutyExchanges: dutyExchanges }, { root: true });
	},

	async socket_updated_Report({ state, commit, dispatch, rootState }, payload) {
		const report = payload[0].payload;

		if (solutionPathRegex.test(router.currentRoute._value.path)) {
			commit('report/updateReport', { report }, { root: true });
		}
	},

	async socket_created_Report({ state, commit, dispatch, rootState }, payload) {
		const report = payload[0].payload;
		let updatedDuty = state.dutyList[report.duty];
		if (solutionPathRegex.test(router.currentRoute._value.path)) {
			commit('report/createReport', { report }, { root: true });
		}
		if (updatedDuty) {
			if (!Object.hasOwn(updatedDuty, 'hasReports')) {
				updatedDuty.hasReports = true;
				if (updatedDuty.user) {
					commit('updateDuty', { duty: updatedDuty, skipUpdate: false });
				} else {
					commit('updateUnallocatedDuties', { duty: updatedDuty });
				}
			}
		}
	},

	async socket_deleted_Report({ state, commit, dispatch, rootState }, payload) {
		const report = payload[0].payload;
		let updatedDuty = state.dutyList[report.duty];
		const updatedReports = rootState.report.dutyReports.filter((item) => item.id !== report.id);
		if (solutionPathRegex.test(router.currentRoute._value.path)) {
			commit('report/deleteReport', { report }, { root: true });
		}

		if (updatedReports.length === 0) {
			if (updatedDuty) {
				updatedDuty.hasReports = false;
				if (updatedDuty.user) {
					commit('updateDuty', { duty: updatedDuty, skipUpdate: false });
				} else {
					commit('updateUnallocatedDuties', { duty: updatedDuty });
				}
			}
		}
	},

	async processSocketQueue({ state, commit, dispatch }) {
		for (const socketQueueItem of state.socketQueue) {
			if (socketQueueItem.event === 'createDuty') {
				await dispatch('socket_created_Duty', socketQueueItem.payload);
			}
			if (socketQueueItem.event === 'updateDuty') {
				await dispatch('socket_updated_Duty', socketQueueItem.payload);
			}
			if (socketQueueItem.event === 'deleteDuty') {
				await dispatch('socket_deleted_Duty', socketQueueItem.payload);
			}
		}
		commit('clearSocketQueue');
	},

	clearHistories({ commit }) {
		commit('setHistories', []);
	},

	async loadDrivers({ state, commit, dispatch }) {
		if (state.drivers.length) {
			return state.drivers;
		}
		try {
			const result = await userApi.listDrivers();
			return result.data;
		} catch (err) {
			dispatch('setError', { error: true, message: err.response.data.message });
		}
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
